import { FC, ReactNode } from 'react';
import { WittySource, WittyToolCall } from '@agilelab/plugin-wb-witty-common';
import { MarkdownContent } from '@mainstage/core-components';
import {
  Box,
  Theme,
  Typography,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import { WittyIcon } from '../icons/WittyIcon';
import { Feedbacks } from './Feedbacks';
import { WbUserAvatar } from '../../WbAvatar';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { ToolActivity } from './ToolActivity';

export interface WittyMessage {
  sender: 'witty' | 'you';
  contentMessage: string;
  responderAgentId?: string;
  responderLabel?: string;
  isFeedbackActive?: boolean;
  isError?: boolean;
  customContent?: ReactNode;
  sources?: WittySource[];
  toolCalls?: WittyToolCall[];
}

type StyleProps = {
  sender: 'witty' | 'you';
  showHeader: boolean;
};

const useStyles = makeStyles<Theme, StyleProps>(
  theme => ({
    container: {
      display: 'flex',
      flexDirection: 'column',
      paddingTop: props => theme.spacing(props.showHeader ? 1 : 0.5),
      alignItems: props => (props.sender === 'you' ? 'flex-end' : 'auto'),
    },
    markdown: {
      flex: 1,
      overflow: 'auto',
      paddingRight: theme.spacing(1),
      paddingLeft: theme.spacing(1),
    },
    userMessage: {
      backgroundColor: theme.palette.bkg.primary,
      width: '100%',
      borderRadius: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
    errorMessage: {
      color: theme.palette.secondary.main,
      fontStyle: 'italic',
      marginTop: theme.spacing(0.5),
    },
    avatar: {
      'div&': {
        width: '24px',
        height: '24px',
      },
    },
    responderName: {
      marginLeft: theme.spacing(0.5),
      fontSize: '0.8125rem',
    },
    sources: {
      margin: theme.spacing(1, 1, 0),
      paddingTop: theme.spacing(1),
      borderTop: `1px solid ${theme.palette.divider}`,
      boxSizing: 'border-box',
      minWidth: 0,
      maxWidth: '100%',
    },
    sourceList: {
      margin: theme.spacing(0.5, 0, 0),
      paddingLeft: theme.spacing(2.5),
      boxSizing: 'border-box',
      minWidth: 0,
      maxWidth: '100%',
    },
    sourceLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      minWidth: 0,
      maxWidth: '100%',
    },
    sourceName: {
      minWidth: 0,
      overflowWrap: 'anywhere',
    },
    sourceIcon: {
      fontSize: '0.875rem',
      flexShrink: 0,
    },
  }),
  { name: 'message' },
);

export const Message: FC<WittyMessage & { showHeader?: boolean }> = ({
  sender,
  contentMessage,
  responderLabel,
  isFeedbackActive = true,
  isError = false,
  customContent,
  sources,
  toolCalls,
  showHeader = true,
}) => {
  const classes = useStyles({ sender, showHeader });
  const theme = useTheme();
  const agentLabel =
    responderLabel && responderLabel.toLocaleUpperCase() !== 'WITTY'
      ? responderLabel
      : undefined;
  let headerIcon: ReactNode = (
    <WbUserAvatar classes={{ avatar: classes.avatar }} singleInitial />
  );
  let headerLabel: ReactNode = `${sender.toLocaleUpperCase()}:`;
  let messageClassName = classes.userMessage;

  if (sender === 'witty') {
    headerIcon = <WittyIcon style={{ color: theme.palette.witboostLogo }} />;
    headerLabel = (
      <>
        WITTY
        {agentLabel && (
          <span className={classes.responderName}>({agentLabel})</span>
        )}
        :
      </>
    );
    messageClassName = '';
  }

  return (
    <Box className={classes.container}>
      {showHeader && (
        <Box
          display="flex"
          alignItems="center"
          style={{ gap: theme.spacing(1) }}
        >
          {headerIcon}
          <Typography style={{ fontWeight: 600 }} variant="body2">
            {headerLabel}
          </Typography>
        </Box>
      )}
      <Box
        className={isError ? classes.errorMessage : messageClassName}
        role={isError ? 'alert' : undefined}
        aria-label={isError ? 'Execution error' : undefined}
      >
        {sender === 'witty' && toolCalls && toolCalls.length > 0 && (
          <Box className={classes.markdown}>
            <ToolActivity toolCalls={toolCalls} />
          </Box>
        )}
        <MarkdownContent
          className={classes.markdown}
          content={contentMessage}
        />
        {sender === 'witty' && sources && sources.length > 0 && (
          <Box className={classes.sources}>
            <Typography variant="body2" component="h3">
              Sources
            </Typography>
            <Box component="ul" className={classes.sourceList}>
              {sources.map(source => (
                <li key={source.uri}>
                  <a
                    className={classes.sourceLink}
                    href={source.uri}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className={classes.sourceName}>{source.name}</span>
                    <OpenInNewIcon
                      className={classes.sourceIcon}
                      aria-hidden="true"
                    />
                  </a>
                </li>
              ))}
            </Box>
          </Box>
        )}
        {customContent}
        {sender === 'witty' && !isError && isFeedbackActive && <Feedbacks />}
      </Box>
    </Box>
  );
};
